import { Input, Spin, Switch, Table } from 'antd';
import { ColumnProps } from 'antd/es/table';
import { useFormik } from 'formik';
import { observer } from 'mobx-react';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { DeleteButton, SaveButton } from '../../components/action-buttons';
import { ImageHolder } from '../../components/image-holder';
import { PageTopBar } from '../../components/page-top-bar';
import { TableHeaderWithSubtitle } from '../../components/table-header-with-subtitle';
import { Tag } from '../../components/tag';
import {
    useAuthStore,
    useCategoryStore,
    usePromotionStore,
} from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import {
    CategoryUpsertPayload,
    YupCategoryUpsertPayload,
} from '../../stores/category';
import { Promotion } from '../../stores/promotion';
import { getItemProp } from '../../utils';
import {
    getPromotionStatus,
    PromotionStatusTag,
} from '../../utils/promotion-status';
import { PromotionTypeTag } from '../../utils/promotion-types';
import { useNavigation } from '../../utils/use-navigation';
import style from './style.module.scss';

// Validation for the expected params
const YupParams = yup.object({
    action: yup.string().oneOf(['create', 'edit']).required(),
    id: yup
        .string()
        .test('shouldBeDefined', 'Id should be defined', function (v) {
            const action = this.resolve(yup.ref('action'));
            return !(v === undefined && action !== 'create');
        }),
});

const renderTitle = () => {
    const TitleComponent = (_: unknown, record: Promotion) => {
        const enTitle = getItemProp(record.items, 'en', 'title');
        return <div className={style.title}>{enTitle}</div>;
    };
    TitleComponent.displayName = 'TitleComponent';
    return TitleComponent;
};

const renderThumbnail = (_: unknown, record: Promotion) => {
    const enImgSrc = getItemProp(record.items, 'en', 'thumbnail');
    return <ImageHolder src={enImgSrc ?? ''} size={70} />;
};

const renderDates = (_: unknown, record: Promotion) => {
    return (
        <div className={'dates_holder'} style={{ padding: '4px 0' }}>
            <div
                className="text_center inline_block"
                style={{ marginRight: '12px' }}
            >
                <div style={{ marginBottom: '2px' }}>
                    {record.startDate.format('ll')}
                </div>
                <div className={'lil_label'}>
                    {record.startDate.format('LT')}
                </div>
            </div>
            {record.endDate != null && (
                <div className="text_center inline_block">
                    <div style={{ marginBottom: '2px' }}>
                        {record.endDate.format('ll')}
                    </div>
                    <div className={'lil_label'}>
                        {record.endDate.format('LT')}
                    </div>
                </div>
            )}
        </div>
    );
};

const renderIsActive = (_: unknown, record: Promotion) => {
    const promotionDateStatus = getPromotionStatus(record);

    return (
        <div className={style.tags_container}>
            {record.isActive ? (
                <Tag variant="success">Active</Tag>
            ) : (
                <Tag variant="error">Inactive</Tag>
            )}
            {promotionDateStatus !== null && (
                <PromotionStatusTag status={promotionDateStatus} />
            )}
        </div>
    );
};

const renderType = (_: unknown, record: Promotion) => {
    return <PromotionTypeTag type={record.type} />;
};

type Params = yup.InferType<typeof YupParams>;

export const CategoryPage = observer(() => {
    const navigation = useNavigation();
    const params = useParams<Params>();
    const categoryStore = useCategoryStore();
    const promotionStore = usePromotionStore();
    const [isFetching, setIsFetching] = React.useState<boolean>(false);
    const [isUpserting, setIsUpserting] = React.useState<boolean>(false);
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
    const authStore = useAuthStore();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const formik = useFormik<CategoryUpsertPayload>({
        initialValues: {
            items: [
                {
                    language: 'en',
                    title: '',
                },
                {
                    language: 'fr',
                    title: '',
                },
            ],
            canBeDeleted: false,
            requireAgeVerification: false,
        },
        validationSchema: YupCategoryUpsertPayload,
        validateOnMount: true,
        onSubmit: () => console.log('Submit!'),
    });

    const saveCategory = useCallback(async () => {
        setIsUpserting(true);
        try {
            if (params.action === 'edit') {
                await categoryStore.updateCategory(
                    formik.values,
                    Number(params.id!)
                );
            } else {
                await categoryStore.createCategory(formik.values);
            }
        } finally {
            setIsUpserting(false);
        }
    }, [categoryStore, formik.values, params.action, params.id]);

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty,
        onSave: saveCategory,
    });

    const createCategory = useCallback(async () => {
        await saveCategory();
        clearDirty(() => navigation.replaceToCategories());
    }, [saveCategory, clearDirty, navigation]);

    const updateCategory = useCallback(async () => {
        await saveCategory();
        clearDirty();
    }, [saveCategory, clearDirty]);

    if (!YupParams.isValidSync(params)) {
        navigation.replaceToCategoryCreate();
    }

    React.useEffect(() => {
        if (params.action === 'edit') {
            setIsFetching(true);
            const categoryId = Number(params.id!);
            categoryStore
                .fetchCategory(categoryId)
                .then((cat) => {
                    console.log('category', cat);
                    formik.resetForm({ values: cat });
                })
                .catch(() => navigation.replaceToCategoryCreate())
                .finally(() => setIsFetching(false));
            promotionStore.fetchForCategory(categoryId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDeleteConfirm = async () => {
        setIsDeleting(true);
        const response = await categoryStore.deleteCategory(Number(params.id!));
        setIsDeleting(false);

        if (!response.err) {
            navigation.replaceToCategories();
        }
    };

    const columns: ColumnProps<Promotion>[] = [
        {
            key: 'is_active',
            render: renderIsActive,
        },
        {
            key: 'image',
            render: renderThumbnail,
        },
        {
            title: 'Title',
            key: 'title',
            render: renderTitle(),
        },
        {
            title: 'Type',
            key: 'type',
            render: renderType,
        },
        {
            title: (
                <TableHeaderWithSubtitle
                    title="Dates"
                    subtitle="Start date / End date"
                />
            ),
            key: 'dates',
            render: renderDates,
        },
    ];

    const renderTopBarActions = () => {
        switch (params.action) {
            case 'create':
                return (
                    <SaveButton
                        onClick={createCategory}
                        loading={isUpserting}
                        disabled={!formik.isValid}
                    />
                );
            case 'edit':
                return (
                    <>
                        {formik.values.canBeDeleted && (
                            <DeleteButton
                                onClick={onDeleteConfirm}
                                loading={isDeleting}
                            />
                        )}
                        <SaveButton
                            onClick={updateCategory}
                            loading={isUpserting}
                            disabled={!formik.isValid}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <PageTopBar
                title={
                    params.action === 'edit'
                        ? `Category #${params.id}`
                        : 'New Category'
                }
                goBack={{
                    callback: () => navigation.goToCategories(),
                    text: 'Back to list',
                }}
            >
                {renderTopBarActions()}
            </PageTopBar>

            {isFetching ? (
                <Spin />
            ) : (
                <>
                    <div className="content_with_margins">
                        <form className="small_form">
                            {formik.values.items.map((item, i) => (
                                <div key={item.language}>
                                    <span>Title {item.language}</span>
                                    <Input
                                        name={`items[${i}].title`}
                                        value={item.title}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            ))}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <Switch
                                    checked={
                                        formik.values.requireAgeVerification
                                    }
                                    onChange={(checked) => {
                                        formik.setFieldValue(
                                            'requireAgeVerification',
                                            checked
                                        );
                                    }}
                                />
                                <span>Require age verification</span>
                            </div>
                        </form>
                        <h2>Promotions of this category</h2>
                    </div>

                    <Table<Promotion>
                        className={'clickable_table_body_rows'}
                        scroll={{ x: 900 }}
                        rowKey="id"
                        dataSource={promotionStore.promotionsForCategory}
                        columns={columns}
                        loading={promotionStore.isFetchingForCategory}
                        pagination={false}
                        onRow={(r) => ({
                            onClick: () => navigation.goToCouponEdit(r.id),
                        })}
                    />
                </>
            )}
        </>
    );
});
